import React, { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { resolveLanguage } from '@/application/i18n/language'
import { investigationSlug } from '@/application/seo/investigation-url'
import { useGetPaginatedInvestigations } from '@/application/services/public-investigation.service'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { markdownToPlainExcerpt } from '@/ui/utils/markdown'
import { ArticleMeta } from './ArticleMeta'

// Roughly the character budget of the ~4 lines `line-clamp-4` used to show.
const EXCERPT_MAX_LENGTH = 220

const PAGE_SIZE = 2
// Fetched once, then paginated client-side (see below) — large enough to
// comfortably cover the `excludeId` case without a second network request.
const FETCH_LIMIT = 100

/**
 * Paginated list (2/page) of investigations filtered by `status`, newest
 * first. Owns its own page state and fetches independently of the homepage
 * feed. Used for both "Enquêtes en cours" (active) and "Enquêtes terminées"
 * (inactive). `excludeId` drops the lead story from the list so it isn't
 * shown twice.
 *
 * Pagination is computed client-side over the full (excludeId-filtered)
 * result rather than from the API's own `page`/`total`: the lead story is
 * always the single newest active investigation, i.e. always item 0 of this
 * exact query, so excluding it after a server-paginated fetch would silently
 * drop one item from page 1 only, desyncing the API's page boundaries from
 * what's actually displayed. Fetching everything once and slicing here keeps
 * every page exactly `PAGE_SIZE` items regardless of where the exclusion falls.
 */
export function HomeInvestigationList({
  status,
  titleKey,
  excludeId,
}: {
  status: 'active' | 'inactive'
  titleKey: string
  excludeId?: string
}) {
  const { t, i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  const [page, setPage] = useState(1)

  const { data, isLoading } = useGetPaginatedInvestigations({
    page: 1,
    limit: FETCH_LIMIT,
    sortBy: 'date',
    sortOrder: 'DESC',
    status,
  })

  const allMatching = (data?.investigations ?? []).filter(
    (investigation) => investigation.id !== excludeId,
  )
  const totalPages = Math.ceil(allMatching.length / PAGE_SIZE)
  const investigations = allMatching.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div>
      <h2 className="font-serif text-xl font-bold text-foreground border-b-2 border-border pb-2 mb-3">
        {t(titleKey)}
      </h2>

      {isLoading ? (
        <div className="space-y-4 py-1">
          {Array.from({ length: PAGE_SIZE }).map((_item, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      ) : investigations.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">{t('home.empty_state')}</p>
      ) : (
        <ul className="divide-y divide-border">
          {investigations.map((investigation) => (
            <li key={investigation.id} className="py-5 first:pt-0">
              <Link
                to="/investigations/$slug"
                params={{ slug: investigationSlug(investigation, locale) }}
                /*
                 * Two different image behaviours depending on where this
                 * list renders relative to the lead story (NewsHomePage):
                 *
                 * Below `xl:`, the list sits BELOW the full-width lead story
                 * (single-column grid), so there's plenty of width to spare.
                 * It's a stretched flex row — `flex-row-reverse` because the
                 * image div is first in the DOM (see below) but must show on
                 * the right — and `items-stretch` makes the image column
                 * exactly as tall as the text column, whatever that turns
                 * out to be: the ROW's height is driven by the text
                 * (categories, title, excerpt), not the other way around.
                 * That column has no fixed width, only `aspect-[4/3]` capped
                 * by `max-w-[30%]`: its width is derived from the stretched
                 * height to keep the photo's 4:3 shape, but never grows past
                 * 30% of the row, so the text always keeps at least ~70%.
                 *
                 * From `xl:` up, the list becomes the narrow ~1/3-width
                 * sidebar column BESIDE the lead story, and there the text
                 * should instead flow around the picture like a newspaper
                 * clipping. `xl:flow-root` switches the row to a normal block
                 * formatting context (containing the float below and letting
                 * it stretch the item's height), and the image div — which
                 * has to come FIRST in the DOM for the text after it to wrap
                 * around it — becomes `xl:float-right` at its own fixed size.
                 */
                className="flex flex-row-reverse items-stretch xl:flow-root gap-6 group no-underline hover:no-underline"
              >
                <div className="aspect-[4/3] max-w-[30%] xl:w-52 xl:max-w-none xl:float-right xl:ml-6 xl:mb-2 shrink-0 bg-muted overflow-hidden rounded-md">
                  <img
                    src={investigation.imageUrl || '/logo.png'}
                    alt={investigation.name}
                    className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${
                      investigation.imageUrl ? 'object-cover' : 'object-contain p-4 opacity-40'
                    }`}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  {investigation.categoryTerms && investigation.categoryTerms.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1.5">
                      {investigation.categoryTerms.map((term) => (
                        <Badge
                          key={term.id}
                          variant="outline"
                          className="text-xs border-primary/30 text-primary"
                        >
                          {term.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <h3 className="font-serif text-xl font-bold text-foreground group-hover:text-primary leading-snug">
                    {investigation.name}
                  </h3>
                  {/*
                   * Truncated by character count via `markdownToPlainExcerpt`
                   * (strips markdown too) rather than CSS `line-clamp`:
                   * `line-clamp` renders via `-webkit-line-clamp`, whose
                   * multi-line truncation box computes its width once, at
                   * whatever inline position it starts, instead of
                   * re-checking the float per line like ordinary wrapped
                   * text does — so once `xl:float-right` applies above, it
                   * could start beside the float and stay that narrow for
                   * every line, even ones actually below the picture, or
                   * cut off mid-line if height-capped instead. A plain,
                   * already-shortened string has neither problem: it's
                   * ordinary flowed text (so it DOES re-check the float per
                   * line), and whatever length it renders at is shown in
                   * full, with no visual clipping on top.
                   */}
                  <p className="mt-2 text-sm text-muted-foreground">
                    {markdownToPlainExcerpt(investigation.description, EXCERPT_MAX_LENGTH)}
                  </p>
                  <ArticleMeta
                    investigation={investigation}
                    showCategories={false}
                    className="mt-2"
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
          >
            ←
          </Button>

          {Array.from({ length: totalPages }, (_item, index) => index + 1)
            .filter(
              (pageNum) => pageNum === 1 || pageNum === totalPages || Math.abs(pageNum - page) <= 2,
            )
            .map((pageNum, index, pageArray) => (
              <React.Fragment key={pageNum}>
                {index > 0 && pageArray[index - 1] !== pageNum - 1 && (
                  <span className="text-muted-foreground px-1">…</span>
                )}
                <Button
                  variant={pageNum === page ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              </React.Fragment>
            ))}

          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={page >= totalPages}
          >
            →
          </Button>
        </div>
      )}
    </div>
  )
}
